import { getMillSensor } from 'attrs/sensorType';

const toOption = (title, extra = {}) => ({
  title,
  value: title,
  ...extra
});

const uniqueBy = (items, getKey) => {
  const seen = new Set();

  return items.filter(item => {
    const key = getKey(item);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const getSensorFilterOption = (sensor, t) => {
  const millSensor = getMillSensor(sensor?.type);
  const title = t(millSensor?.name || sensor?.custom_sensor_name || sensor?.static_sensor_name || sensor?.type);

  return {
    title,
    value: sensor?.type,
    type: sensor?.type
  };
};

export const getDialogFilterOptions = ({
  productionLines = [],
  selectedProductLines = [],
  selectedMachines = [],
  t
}) => {
  const selectedProductLineValues = new Set(selectedProductLines.map(item => item.value));
  const selectedMachineIds = new Set(selectedMachines.map(item => item.id));

  const productLineOptions = uniqueBy(
    productionLines
      .map(line => line?.name)
      .filter(Boolean)
      .map(lineName => toOption(lineName)),
    option => option.value
  );

  const machinesScope =
    selectedProductLineValues.size > 0
      ? productionLines.filter(line => selectedProductLineValues.has(line.name))
      : productionLines;

  const machineOptions = uniqueBy(
    machinesScope.flatMap(line =>
      (line.machines || [])
        .filter(machine => machine?.id && machine?.name)
        .map(machine => ({
          id: machine.id,
          title: machine.name,
          value: machine.name
        }))
    ),
    option => option.id
  );

  const sensorsScope =
    selectedMachineIds.size > 0
      ? machinesScope.map(line => ({
          ...line,
          machines: (line.machines || []).filter(machine => selectedMachineIds.has(machine.id))
        }))
      : machinesScope;

  const sensorOptions = uniqueBy(
    sensorsScope.flatMap(line =>
      (line.machines || []).flatMap(machine => (machine.sensors || []).map(sensor => getSensorFilterOption(sensor, t)))
    ),
    option => option.type
  );

  return {
    productLineOptions,
    machineOptions,
    sensorOptions
  };
};

export const filterTreeBySelections = ({
  productionLines = [],
  selectedProductLines = [],
  selectedMachines = [],
  selectedSensors = []
}) => {
  const selectedProductLineValues = new Set(selectedProductLines.map(item => item.value));
  const selectedMachineIds = new Set(selectedMachines.map(item => item.id));
  const selectedSensorTypes = new Set(selectedSensors.map(item => item.type || item.value));

  return productionLines.reduce((linesAcc, line) => {
    if (selectedProductLineValues.size > 0 && !selectedProductLineValues.has(line.name)) {
      return linesAcc;
    }

    const machines = (line.machines || []).reduce((machinesAcc, machine) => {
      if (selectedMachineIds.size > 0 && !selectedMachineIds.has(machine.id)) {
        return machinesAcc;
      }

      const sensors =
        selectedSensorTypes.size > 0
          ? (machine.sensors || []).filter(sensor => selectedSensorTypes.has(sensor.type))
          : machine.sensors || [];

      if (sensors.length === 0) {
        return machinesAcc;
      }

      machinesAcc.push({
        ...machine,
        sensors
      });

      return machinesAcc;
    }, []);

    if (machines.length === 0) {
      return linesAcc;
    }

    linesAcc.push({
      ...line,
      machines
    });

    return linesAcc;
  }, []);
};

export const keepSameArrayIfUnchanged = (previousItems = [], nextItems = [], getKey = item => item) => {
  if (previousItems.length !== nextItems.length) {
    return nextItems;
  }

  const hasChanges = previousItems.some((item, index) => getKey(item) !== getKey(nextItems[index]));

  return hasChanges ? nextItems : previousItems;
};
